import { isProductKind } from '@iheartjane/types';
import cloneDeep from 'lodash-es/cloneDeep';
import { useCallback } from 'react';

import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import { useSearchContext } from '@jane/kiosk/providers';
import type { MenuCategory } from '@jane/kiosk/util';
import { getSubcategories } from '@jane/kiosk/util';
import type { ProductFacets, FilterSelectItem } from '@jane/search/data-access';
import {
  formatActiveFilters,
  handleFilterChange,
  overrideCustomCategoryLabels,
} from '@jane/search/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import { QuickFilterButton } from '../QuickFilterButton/QuickFilterButton';

interface SubcategoryQuickFilterProps {
  category: MenuCategory;
  filteredProductCount: number;
  presentedFacets?: ProductFacets;
  totalProductCount: number;
}

export const SubcategoryQuickFilter = ({
  category,
  filteredProductCount,
  presentedFacets,
  totalProductCount,
}: SubcategoryQuickFilterProps) => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const subcategories = getSubcategories(presentedFacets ?? {}, store);
  const showSubcategories = isProductKind(category);
  const subcategoryOptions =
    (showSubcategories && subcategories[category]) || [];
  const { resetSearchState, searchState, setSearchState } = useSearchContext();

  const onClearAllQuickFilters = useCallback(() => {
    const newState = cloneDeep(searchState);
    if (newState.filters?.root_types) {
      newState.filters.root_types = [];
    }

    resetSearchState(newState);
  }, [resetSearchState, searchState]);

  const onSubcategorySelected = useCallback(
    (option: FilterSelectItem) => {
      return setSearchState(
        handleFilterChange('root_types', option.value, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const activeRootTypeFilters = formatActiveFilters(searchState, {
    customLabels: {
      category: store?.custom_lineage_labels,
      root_types: overrideCustomCategoryLabels(
        store?.state === 'California',
        store?.custom_product_type_labels
      ),
    },
    storeId: store?.id,
  }).filter(({ key }) => key === 'root_types');

  if (!showSubcategories) {
    return null;
  }

  return (
    <QuickFilterButton
      activeFilters={activeRootTypeFilters}
      label="Subcategory"
      onClearAll={onClearAllQuickFilters}
      onOptionSelect={onSubcategorySelected}
      options={subcategoryOptions}
      filteredProductCount={filteredProductCount}
      totalProductCount={totalProductCount}
      store={store}
    />
  );
};
